import { z } from 'zod';
import {
  ApiError,
  createTransport,
  passkeyListSchema,
  passkeyRemovedSchema,
  passkeyRenamedSchema,
  rememberSessionHandle,
} from '@bp-files/client';
import { env } from '@/env';

/**
 * This app's API contract.
 *
 * **The transport moved to `@bp-files/client`; the contract stayed here**, and
 * that split is the point rather than an artefact of the extraction. Two apps
 * share one `request()` — one place where the `Authorization` header goes last,
 * one place that turns a token failure into a 401 rather than an offline error.
 * Neither app shares the other's endpoints: this file knows about folders and
 * the roster, the operator console knows about the fleet, and a package that
 * knew about either would be one both apps had to agree about.
 *
 * Every response is parsed with a Zod schema at the boundary, so a backend
 * shape change surfaces here with a clear message instead of as `undefined`
 * deep inside a component.
 *
 * Do not mock this module in tests — mock the network with MSW. Tests then
 * exercise the real parsing and error handling, which is where the bugs are.
 */

/**
 * Bound to this app's API origin at module load.
 *
 * A factory rather than a configured global: there is no `request` to call
 * until you have made one, so the base URL cannot be forgotten the way a
 * `configureClient()` at startup could be.
 */
const { request, postJson, putJson, patchJson, delJson } = createTransport(env.VITE_API_BASE_URL);

/**
 * Exported for `lib/bpSession.ts`, and for nothing else.
 *
 * **The one exception to "this file owns the contract".** Every other endpoint
 * in this app is a named function below with its own schema, because a caller
 * that can post anywhere is a caller that can invent a contract. The session
 * routes are different in kind: their schemas live in `@bp-files/client`
 * alongside the code that reads the cookie, because both apps will need them
 * and neither of them is about folders or the roster. So the package owns the
 * shape and this app lends it the transport it is already bound to.
 */
export { postJson };

/**
 * Re-exported so every existing importer is unaffected by the move.
 * `messageFrom` in `@bp-files/client` reads its `body`, and components catch it
 * by name — both of which were true before and are the reason this is a
 * re-export rather than a rename.
 */
export { ApiError };

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/*
 * `GET /health` had a client here, and a `HealthCard` that rendered it on the
 * Files surface. The endpoint is still real and still an availability probe —
 * it is simply not something a browser session has any use for, and putting its
 * output on an operational screen told an operator about the build rather than
 * about their files. Both went; the endpoint stayed.
 */

/**
 * `GET /me` — mirrors `MeBody` in apps/api/src/functions/me.
 *
 * `role` is the **only** representation of what the caller may do. There is no
 * `isAdmin`; derive it (`role === 'admin'`) rather than asking the server for
 * the same fact twice. And note what this does not authorize: SEC-5 puts
 * authorization in the API, so this drives what the UI shows, never what it
 * is allowed to do.
 */
export const meSchema = z.object({
  user: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    status: z.enum(['Invited', 'Active', 'Revoked']),
  }),
  agency: z.object({
    id: z.string(),
    name: z.string(),
    domain: z.string(),
    /**
     * CON-3, as amended. **`Free` is superseded by two states, not one:**
     * `Trial` is a customer in its free first year and `Receiving` is an agency
     * that never subscribed, and they do not behave alike — a Trial agency
     * connects and uploads (CON-7a).
     *
     * Hand-mirrored from `AGENCY_STATES` in the API's `types/domain.ts`; there
     * is no shared types package. The API normalises a legacy `Free` row to
     * `Receiving` on read, which is what lets this enum stay closed.
     */
    state: z.enum(['Receiving', 'Provisioning', 'Trial', 'Subscribed']),
    /**
     * PLN-1's two plans. **Optional, and absent is meaningful**: an agency that
     * holds no plan — `Receiving`, or still `Provisioning` — has no `plan` on
     * its row, and a default of `Docs` here would be a value nobody bought.
     *
     * Hand-mirrored from `PLANS` in the API's `types/domain.ts`, like `state`
     * above; there is no shared types package.
     */
    plan: z.enum(['Docs', 'DocsEvidence']).optional(),
  }),
  role: z.enum(['member', 'admin']),
  /**
   * The spaces this caller may open, each with PERM-1a's access level.
   *
   * Hand-mirrored from `GRANT_MODES` in the API's `types/domain.ts`, like
   * `state` and `plan` above. `write` means read **and** write; there is no
   * write-without-read, because §5.3 gives browse and download to every grant
   * holder.
   *
   * Pairs rather than a map because JSON has none. An admin's list is their
   * agency's whole `Active` set at `write` (PERM-2).
   */
  grants: z.array(z.object({ spaceId: z.string(), mode: z.enum(['read', 'write']) })),
  /**
   * Whether this session may reach the product, or only the account screen
   * (SYS-10).
   *
   * **The one field here that describes the _session_ rather than the person.**
   * It is on `/me` because the answer is needed on every load and not only at
   * the exchange that produced the session — a tab reopened an hour later asks
   * again.
   *
   * As ever, navigation and not authorization: `withAuth` refuses the same
   * caller whatever this app does with the value (SEC-5).
   */
  restricted: z.boolean(),
  /**
   * How many credentials this person holds (USR-12).
   *
   * **Zero is the whole signal**, and it needs no companion: you cannot have
   * signed in with a passkey you do not have, so a count of zero already means
   * the session was bootstrapped by the SYS-7 passcode. `restricted` answers
   * the neighbouring question — bootstrapped *while holding* credentials — so
   * between them the two cover every case without this app ever seeing
   * `assurance`.
   */
  passkeyCount: z.number().int().nonnegative(),
  /**
   * Whether this person has never told us what to call them (USR-1).
   *
   * **Derived by the API, not compared here.** The rule is "the roster still
   * holds the address USR-2 recorded", and it belongs beside the code that
   * writes a name — a `user.name === user.email` check in this file would be a
   * second definition, free to disagree the day the invitation stops seeding
   * the address.
   *
   * **It exists because Entra will not collect a display name for us.** An
   * External ID sign-up gathers none, so a local account arrives claiming
   * `"unknown"` and the API drops it — which is why the landing page asks
   * rather than reading a token claim (ADR-0038, superseded on provenance).
   *
   * Navigation, not authorization: ignoring it just leaves the address showing.
   */
  needsName: z.boolean(),
  /**
   * Whether an admin has authorised this account to acquire a credential
   * (USR-14).
   *
   * **The sixth fact the landing page decides from** (SYS-8, ADR-0051). One of
   * its four acts is *add a passkey*, and offering that to a locked account is
   * offering a button the API refuses — worst of all to a `restricted` session,
   * whose only exit that is.
   *
   * **A fact about the account, not the session**, unlike `restricted` above:
   * it can be true for somebody already holding several credentials, because an
   * admin may have unlocked them for a second device.
   *
   * Visibility, not authorization. `apps/api`'s `shared/enrollment.ts` is the
   * boundary (SEC-5); this only decides what the page offers.
   */
  enrollmentUnlocked: z.boolean(),
});

export type Me = z.infer<typeof meSchema>;

export function getMe(signal?: AbortSignal): Promise<Me> {
  return request('/me', meSchema, signal ? { signal } : undefined);
}

// ---------------------------------------------------------------------------
// The passcode — SPEC §3.2 SYS-7, §12
// ---------------------------------------------------------------------------

/*
  **The one credential this app drives itself, end to end.** Everything else
  about signing in is either the browser's (a passkey ceremony) or was
  Microsoft's (an MSAL redirect); this is three ordinary POSTs and a form.

  The schemas live here rather than in `@bp-files/client` because `apps/admin`
  will never call them — that package holds shapes both apps need, and this is
  `apps/web`'s contract, exactly as folders and the roster are.

  **`flow` is opaque and stays in memory.** It is a handle to a server-side row
  holding an Entra continuation token, and it is deliberately not a cookie: a
  sign-in that survived a reload would be a credential outliving the tab that
  earned it.
*/

export const passcodeSentSchema = z.object({
  flow: z.string(),
  /** How many digits to accept. Read from the directory, not assumed. */
  codeLength: z.number().int().positive(),
});

export type PasscodeSent = z.infer<typeof passcodeSentSchema>;

export const passcodeResentSchema = z.object({
  codeLength: z.number().int().positive(),
});

/** Same shape the passkey assertion returns, so the page reads one thing. */
export const passcodeSignedInSchema = z.object({
  restricted: z.boolean(),
  /**
   * The session handle, present only where a cookie cannot exist (ADR-0059).
   *
   * Optional because it is absent in every one-origin build — which is prod,
   * and every mocked build — where the `Set-Cookie` on the same response is the
   * whole credential and this field never appears on the wire.
   */
  session: z.string().optional(),
});

export type PasscodeSignedIn = z.infer<typeof passcodeSignedInSchema>;

export const namedSchema = z.object({ name: z.string() });

export function startPasscode(email: string): Promise<PasscodeSent> {
  return postJson('/auth/passcode/start', passcodeSentSchema, { email });
}

/**
 * Trade a code for a session.
 *
 * **The one endpoint function in this file with a side effect, and it is
 * deliberate** (ADR-0059). Where the response carries a handle, this is the
 * moment it exists, and remembering it here rather than at the call site is what
 * keeps `PasscodeForm` from having to know that some deployments cannot hold a
 * cookie. `rememberSessionHandle` ignores `undefined`, so on every one-origin
 * build — prod included — this line does nothing at all.
 */
export async function verifyPasscode(flow: string, code: string): Promise<PasscodeSignedIn> {
  const signedIn = await postJson('/auth/passcode/verify', passcodeSignedInSchema, { flow, code });

  rememberSessionHandle(signedIn.session);

  return signedIn;
}

export function resendPasscode(flow: string): Promise<z.infer<typeof passcodeResentSchema>> {
  return postJson('/auth/passcode/resend', passcodeResentSchema, { flow });
}

/** Once, during onboarding. A second call is a 409 (USR-1). */
export function setMyName(name: string): Promise<z.infer<typeof namedSchema>> {
  return putJson('/me/name', namedSchema, { name });
}

// ---------------------------------------------------------------------------
// Passkeys — SPEC §9.3, USR-11
// ---------------------------------------------------------------------------

/*
  **The schemas come from `@bp-files/client`, unlike every other endpoint in
  this file.** Folders and the roster are this app's domain and a package that
  knew about them would be one both apps had to agree about; auth routes are the
  other case — the package is where the credential code lives, both apps will
  reach them eventually, and none of them is about anybody's files. Enrolment
  itself is not here at all: it is a ceremony rather than a request, so it goes
  through `authDriver`, which is what keeps the WebAuthn library out of a mocked
  build.
*/

export function getPasskeys(signal?: AbortSignal): Promise<PasskeyListBody> {
  return request('/auth/passkeys', passkeyListSchema, signal ? { signal } : undefined);
}

export function renamePasskey(credentialId: string, label: string): Promise<PasskeyRenamedBody> {
  return patchJson(`/auth/passkeys/${encodeURIComponent(credentialId)}`, passkeyRenamedSchema, {
    label,
  });
}

export function deletePasskey(credentialId: string): Promise<PasskeyRemovedBody> {
  return delJson(`/auth/passkeys/${encodeURIComponent(credentialId)}`, passkeyRemovedSchema);
}

/**
 * `GET|PUT|DELETE /me/signature` — USR-13, mirrored from
 * `apps/api/src/functions/signature*`.
 *
 * **`ink` is a string on the wire and stays one here.** It is the canonical
 * JSON the server hashed, so parsing and re-serializing it anywhere in this
 * client would produce something whose digest no longer matches the
 * `inkSha256` beside it. `components/Signature/signatureInk.ts` parses it at
 * the point of drawing, and nothing else does.
 *
 * **The clickwrap comes back on every read**, because the screen needs it in
 * both states: an empty one has to show the statement before there is anything
 * to save, and a populated one has to be able to say whether what was accepted
 * is still current.
 */
const signatureViewSchema = z.object({
  ink: z.string(),
  inkSha256: z.string(),
  aspect: z.number(),
  pointCount: z.number().int().nonnegative(),
  clickwrapVersion: z.number().int().positive(),
  acceptedAt: z.string(),
  createdAt: z.string(),
});

const signatureBodySchema = z.object({
  signature: signatureViewSchema.nullable(),
  clickwrap: z.object({ version: z.number().int().positive(), text: z.string() }),
});

const signatureSavedSchema = z.object({ signature: signatureViewSchema });
const signatureRemovedSchema = z.object({ signature: z.null() });

export function getSignature(signal?: AbortSignal): Promise<SignatureBody> {
  return request('/me/signature', signatureBodySchema, signal ? { signal } : undefined);
}

export function saveSignature(ink: unknown, acceptedVersion: number): Promise<SignatureSavedBody> {
  return putJson('/me/signature', signatureSavedSchema, { ink, acceptedVersion });
}

export function removeSignature(): Promise<SignatureRemovedBody> {
  return delJson('/me/signature', signatureRemovedSchema);
}

export type SignatureView = z.infer<typeof signatureViewSchema>;
export type SignatureBody = z.infer<typeof signatureBodySchema>;
export type SignatureSavedBody = z.infer<typeof signatureSavedSchema>;
export type SignatureRemovedBody = z.infer<typeof signatureRemovedSchema>;

/**
 * `GET /me/notifications` and the two subscription routes — NTF-1 and NTF-4,
 * mirrored from `apps/api/src/functions/{meNotifications,folderSubscribe,
 * folderUnsubscribe}`.
 *
 * **The statement comes back on every read**, for the signature's reason: the
 * dialog has to show the current wording, and a client holding an acceptance
 * has to be able to *compare* rather than assume. The version alone cannot say
 * whether to ask again.
 *
 * **`accepted` is nullable and is not the same question as "is this folder
 * subscribed".** One is per person and lives here; the other is per folder and
 * rides on the folder listing (`folderSummarySchema.subscribed`). Conflating
 * them would put a consent dialog in front of a switch somebody has already
 * used on another folder.
 */
const notificationConsentSchema = z.object({
  version: z.number().int().positive(),
  sha256: z.string(),
  acceptedAt: z.string(),
});

const notificationsBodySchema = z.object({
  clickwrap: z.object({ version: z.number().int().positive(), text: z.string() }),
  accepted: notificationConsentSchema.nullable(),
});

const subscriptionViewSchema = z.object({
  folderId: z.string(),
  subscribedAt: z.string(),
});

const subscriptionSchema = z.object({ subscription: subscriptionViewSchema });
/** A delete answers 200 with the new state, never 204 — see ADR-0055. */
const unsubscribedSchema = z.object({ subscription: z.null() });

export function getNotifications(signal?: AbortSignal): Promise<NotificationsBody> {
  return request('/me/notifications', notificationsBodySchema, signal ? { signal } : undefined);
}

export function subscribeToFolder(
  spaceId: string,
  folderId: string,
  acceptedVersion: number,
): Promise<SubscriptionBody> {
  return putJson(
    `/spaces/${encodeURIComponent(spaceId)}/folders/${encodeURIComponent(folderId)}/subscription`,
    subscriptionSchema,
    { acceptedVersion },
  );
}

export function unsubscribeFromFolder(
  spaceId: string,
  folderId: string,
): Promise<UnsubscribedBody> {
  return delJson(
    `/spaces/${encodeURIComponent(spaceId)}/folders/${encodeURIComponent(folderId)}/subscription`,
    unsubscribedSchema,
  );
}

/**
 * FIL-1d's complete, caller-visible destination read model.
 *
 * **A destination is a folder or a space's root** (FIL-1e), and `isRoot` is what
 * says which. Do not infer it by comparing `folderName` to `spaceName` — a root
 * carries its space's name, so the two are equal, but so are they for a folder
 * somebody named after the room.
 */
const folderDestinationSchema = z.object({
  spaceId: z.string(),
  spaceName: z.string(),
  folderId: z.string(),
  folderName: z.string(),
  isRoot: z.boolean(),
  /** Whether the caller may add here — the space picker for FIL-3 is built from it. */
  canWrite: z.boolean(),
  fileCount: z.number(),
  totalBytes: z.number(),
  lastActivityAt: z.string(),
});
const folderDestinationsSchema = z.object({ folders: z.array(folderDestinationSchema) });

export function getFolderDestinations(signal?: AbortSignal): Promise<FolderDestinationsBody> {
  return request('/me/folders', folderDestinationsSchema, signal ? { signal } : undefined);
}

export type FolderDestination = z.infer<typeof folderDestinationSchema>;
export type FolderDestinationsBody = z.infer<typeof folderDestinationsSchema>;

export type NotificationsBody = z.infer<typeof notificationsBodySchema>;
export type SubscriptionBody = z.infer<typeof subscriptionSchema>;
export type UnsubscribedBody = z.infer<typeof unsubscribedSchema>;

export type PasskeyListBody = z.infer<typeof passkeyListSchema>;
export type PasskeyRenamedBody = z.infer<typeof passkeyRenamedSchema>;
export type PasskeyRemovedBody = z.infer<typeof passkeyRemovedSchema>;

/**
 * `GET /spaces` — mirrors `SpacesBody` in apps/api/src/functions/spaces.
 *
 * FIL-1's space list. Two things about it are easy to get wrong from the client
 * side:
 *
 * - **This is not `me.grants`.** That list is Active-only; this one includes
 *   `Invited` and `Removed` memberships for an admin, because CON-6 and CON-14
 *   both need them visible. Do not reconcile the two.
 * - **`participation` is relative to the viewer.** The same space reads
 *   `contributor` to an agency that holds a plan and `observer` to one that does
 *   not (CON-13), so it is never a property of the space in the abstract.
 * - **`status` is this agency's own.** A space has no single status: a room can
 *   hold three `Active` members, two `Invited` and one `Removed` at once
 *   (CON-23).
 */
/**
 * CON-21's closed five-value taxonomy. Hand-mirrored from `AGENCY_TYPES` in
 * the API's `types/domain.ts`, like `state` above; there is no shared types
 * package.
 */
const agencyTypeSchema = z.enum([
  'LawEnforcement',
  'Prosecution',
  'Defense',
  'Court',
  'ClerkOfCourt',
]);

/**
 * CON-24's two values, hand-mirrored from `VISIBILITY_SCOPES` in the API's
 * `types/domain.ts` exactly as `agencyTypeSchema` is.
 *
 * **Exported because it is the wire, and the UI does not say either word.** The
 * roster renders and the two controls submit these strings; what a person reads
 * is `SPACE_LIST_COPY.members.scopes`, which names the consequence rather than
 * the value. Keeping the two apart is what lets the copy be rewritten without
 * touching a request body — and there is no third value to add here, which is
 * ADR-0067's whole argument.
 */
export const VISIBILITY_SCOPES = ['all', 'own'] as const;

export type VisibilityScope = (typeof VISIBILITY_SCOPES)[number];

/**
 * One agency on a space's roster (CON-11 as amended, CON-21).
 *
 * **This replaced `counterpart`, and the widening was deliberate**: a member
 * sees which agencies are in the room with their names, domains and types,
 * because being in a named room with named parties is the fact an agency agreed
 * to when it accepted (CON-6). It stops there — no people, no grants, no other
 * spaces.
 */
export const spaceMemberRefSchema = z.object({
  agencyId: z.string(),
  name: z.string(),
  domain: z.string(),
  /**
   * **Optional here even though the API always sends it.** The type is
   * display-only (CON-21) and treating it as optional keeps every fixture in
   * this workspace that predates the field parsing exactly as it did. A list
   * that renders it treats absence as "no icon", never as an error.
   */
  agencyType: agencyTypeSchema.optional(),
  /** Per member (CON-23). A space has no single status. */
  status: z.enum(['Invited', 'Active', 'Removed']),
  /**
   * What this member agency reads in this room (CON-24 as amended).
   *
   * **On every member, not only on the reader's own row.** An agency deciding
   * whether to put evidence in a room needs to know who will be reading it, and
   * the reassuring reading is the common one — a police agency learning that its
   * neighbours are `own`-scope has learned that they are not reading its booking
   * sheets.
   *
   * **`default`, not `optional`, and the difference is the whole point.**
   * `agencyType` above is optional because an absent type has a sensible
   * rendering — no icon — and every consumer branches on it. An absent scope has
   * no rendering at all: the badge answers a question a reader now opens this
   * panel to ask, and "no badge" would be a reader guessing which of two answers
   * it meant. So the field is optional **on the wire** and total **after
   * parsing**, and the value filled in is `all` — which is not a client-side
   * guess but the same reading `visibilityScopeOf` applies in the API to a
   * membership written before the column existed. There is one interpretation of
   * absence in this system and both ends hold it.
   *
   * The value, never the control: only the owner may change one (§5.3), and the
   * API refuses everybody else whatever a client rendered (SEC-5).
   */
  visibilityScope: z.enum(VISIBILITY_SCOPES).default('all'),
  /** The agency that opened the room and alone administers it (CON-22). */
  isOwner: z.boolean(),
  /**
   * EXIT-7 — when **this** agency's subscription term ends (A-8).
   *
   * **One per member, and it replaced `SpaceSummary.counterpartTermEndsAt`.**
   * That was one date per space, resolved server-side through "the one other
   * agency" and absent in a room of three; a room has a roster, so the date sits
   * beside each agency on it. Projected from that agency's own row rather than
   * stored on the membership (§11) — absent whenever it holds no plan, and shown
   * even when it has already passed, because "their subscription ended" is
   * exactly the signal this carries.
   *
   * **The members panel is its only surface.** It is deliberately not on the
   * card or in the space header — FIL-1 and FIL-2 both judged it noise there.
   */
  termEndsAt: z.string().optional(),
});

export type SpaceMemberRef = z.infer<typeof spaceMemberRefSchema>;

export const spaceSummarySchema = z.object({
  id: z.string(),
  /**
   * What the room is called (CON-22) — its title on every surface.
   *
   * **Required, unlike `agencyType` below**, and the difference is what absence
   * would mean. A missing type renders as no icon; a missing name renders as no
   * title, on a card whose whole job is to be recognised. The API has always
   * sent something to title a space with — until this slice it was the
   * counterpart's name, assembled client-side — so there is no older payload
   * this refuses that a person could usefully be shown.
   */
  name: z.string(),
  members: z.array(spaceMemberRefSchema),
  status: z.enum(['Invited', 'Active', 'Removed']),
  participation: z.enum(['contributor', 'observer']),
  /** Absent on a space that has never been used. */
  lastActivityAt: z.string().optional(),
  /**
   * The room's active window in days (RET-12). **Absent means the 180-day
   * default** — the client reads absence as 180, so an untouched room and an
   * explicit shorten are distinguishable. Shown to every member; only the owner
   * may change it.
   */
  expiryWindowDays: z.number().int().optional(),
  awaitingOurAcceptance: z.boolean(),
  /*
    **USR-10's `recipientsMigratedAt` / `recipientsMigratedCount` stood here and
    are withdrawn with the requirement** (ADR-0072). Nobody manages another
    agency's people, so CON-20 hands over nothing and there is no outcome for a
    space card to carry; §11 drops both columns.
  */
  /*
    **EXIT-7 moved onto the roster.** `counterpartTermEndsAt` was one date per
    space and had no subject in a room of three; it is `members[].termEndsAt`
    now, one per agency, read by the members panel and nowhere else.
  */
});

export type SpaceSummary = z.infer<typeof spaceSummarySchema>;

export const spacesSchema = z.object({ spaces: z.array(spaceSummarySchema) });

export type Spaces = z.infer<typeof spacesSchema>;

export function getSpaces(signal?: AbortSignal): Promise<Spaces> {
  return request('/spaces', spacesSchema, signal ? { signal } : undefined);
}

/**
 * `GET /users` ⚑A — mirrors `UsersBody` in apps/api/src/functions/users.
 *
 * USR-1's Members tab. Three things about it are easy to get wrong from the
 * client side:
 *
 * - **A chip follows the space, not the grant row.** The API drops a grant whose
 *   space is `Requested` or `Severed` (CON-6, CON-14), so an empty `grants` is a
 *   member who can reach nothing right now — not a member with no grant rows.
 * - **An admin's chips are their agency's Active spaces**, despite an admin
 *   holding no grant rows at all (PERM-2). Do not reconcile the two counts.
 * - **`isAdmin` here is a row attribute**, unlike `/me`, which exposes only the
 *   caller's own `role`. USR-5 will toggle this one per member.
 *
 * Admin-only, so a member gets a 403 — which the surface reads to say why.
 */
/**
 * PERM-1a's two levels, hand-mirrored from `GRANT_MODES` in the API's
 * `types/domain.ts` — there is no shared types package.
 *
 * `write` means read **and** write; §5.3 gives browse and download to every
 * grant holder, so there is no write-without-read to name.
 */
export const grantModeSchema = z.enum(['read', 'write']);

export type GrantMode = z.infer<typeof grantModeSchema>;

export const grantChipSchema = z.object({
  spaceId: z.string(),
  /** The room's name (CON-22). It replaced the counterpart's. */
  spaceName: z.string(),
  /** `write` on every chip of an admin's synthesized list (PERM-2). */
  mode: grantModeSchema,
});

export const memberSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  /** USR-1's optional cell. */
  cell: z.string().optional(),
  status: z.enum(['Invited', 'Active', 'Revoked']),
  isAdmin: z.boolean(),
  grants: z.array(grantChipSchema),
  /** Absent for a member who has never signed in. */
  lastActiveAt: z.string().optional(),
  /**
   * How many passkeys this person holds (USR-12).
   *
   * **A count, never a credential.** What model of authenticator somebody
   * carries is not their agency's business — USR-11 shows that to the person it
   * belongs to. This answers the only question an admin has: can they get in.
   */
  passkeyCount: z.number(),
  /** When somebody last reset them (USR-11a rungs 2 and 3). Absent when nobody has. */
  passkeysResetAt: z.string().optional(),
  /**
   * Whether an admin has authorised this account to acquire one credential
   * (USR-14).
   *
   * **The Enrollment toggle's value, and it moves without anybody touching
   * it**: enrolling a passkey spends the unlock, so the server's copy is
   * routinely ahead of the browser's. That is the reason this surface refetches
   * rather than patching, and the reason the toggle is not optimistic.
   */
  enrollmentUnlocked: z.boolean(),
});

export type Member = z.infer<typeof memberSummarySchema>;

/**
 * `POST /users/{id}/passkeys/reset` ⚑A — USR-11a rung 2.
 *
 * Destroys every credential on the account and ends its sessions. Proofing is
 * out-of-band and deliberately unspecified (USR-12): an agency admin knows
 * their officers, and a phone call is the mechanism.
 */
export function resetMemberPasskeys(userId: string): Promise<PasskeyResetResult> {
  return postJson(
    `/users/${encodeURIComponent(userId)}/passkeys/reset`,
    passkeyResetSchema,
    undefined,
  );
}

const passkeyResetSchema = z.object({
  credentialsRemoved: z.number(),
  sessionsEnded: z.number(),
});

export type PasskeyResetResult = z.infer<typeof passkeyResetSchema>;

export const usersSchema = z.object({ members: z.array(memberSummarySchema) });

export type Users = z.infer<typeof usersSchema>;

export function getUsers(signal?: AbortSignal): Promise<Users> {
  return request('/users', usersSchema, signal ? { signal } : undefined);
}

/**
 * The four member-standing actions (USR-3, USR-5, CON-10).
 *
 * One response shape for all four — the two fields any of them can move — for
 * the same reason accept and sever share `spaceActionSchema`: they are the same
 * kind of event from different directions, and the caller's next act is a
 * refetch regardless.
 *
 * **A `last_admin` refusal arrives as a 409 whose message is worth showing.**
 * Unlike most failures here, it names something the admin can actually do —
 * designate another admin first — so the surface reads `ApiError.body` rather
 * than falling back to a generic string (CON-10).
 */
export const memberStandingSchema = z.object({
  userId: z.string(),
  status: z.enum(['Invited', 'Active', 'Revoked']),
  isAdmin: z.boolean(),
});

export type MemberStanding = z.infer<typeof memberStandingSchema>;

export function revokeMember(userId: string): Promise<MemberStanding> {
  return postJson(`/users/${encodeURIComponent(userId)}/revoke`, memberStandingSchema);
}

export function reactivateMember(userId: string): Promise<MemberStanding> {
  return postJson(`/users/${encodeURIComponent(userId)}/reactivate`, memberStandingSchema);
}

export function designateAdmin(userId: string): Promise<MemberStanding> {
  return postJson(`/users/${encodeURIComponent(userId)}/admin`, memberStandingSchema);
}

export function revokeAdmin(userId: string): Promise<MemberStanding> {
  return delJson(`/users/${encodeURIComponent(userId)}/admin`, memberStandingSchema);
}

const memberEnrollmentSchema = z.object({
  userId: z.string(),
  enrollmentUnlocked: z.boolean(),
});

export type MemberEnrollment = z.infer<typeof memberEnrollmentSchema>;

/**
 * `POST` / `DELETE /users/{id}/enrollment` — authorise one credential, or
 * withdraw the authorisation (USR-14).
 *
 * **One function, two verbs**, on `designateAdmin`/`revokeAdmin`'s own split
 * read from the other side: the API keeps them apart so the act shows in the
 * method, and the client joins them because the caller is one toggle whose two
 * directions are the same intent. A boolean parameter here and two routes there
 * is the shape that gives both what they need.
 *
 * **What it grants is one passkey, not a window.** Enrolling spends the unlock,
 * so an admin clicking this is authorising a credential — which is why the
 * value can come back `false` from a refetch nobody triggered.
 */
export function setMemberEnrollment(userId: string, unlocked: boolean): Promise<MemberEnrollment> {
  const path = `/users/${encodeURIComponent(userId)}/enrollment`;
  return unlocked ? postJson(path, memberEnrollmentSchema) : delJson(path, memberEnrollmentSchema);
}

/**
 * `PUT /users/{id}/grants` — set a member's space access (USR-4).
 *
 * The whole set, not a delta, which is why it is a `PUT`. The API reconciles it
 * against the agency's **Active** spaces and leaves grants on `Requested` or
 * `Severed` spaces alone — those have no chip, so the editor could not have shown
 * them, and a wholesale replacement would revoke access nobody could see.
 *
 * Each entry carries an access level (PERM-1a). A space named twice at the
 * **same** level still collapses; naming it twice at different levels is a 400,
 * because that is a contradiction rather than a repetition.
 *
 * **A `write` level on a space this agency cannot send into is accepted, not
 * refused.** The editor shows that option as unavailable, but the API stores the
 * level regardless so CON-20's flip needs no re-grant. Presentation and
 * authorization are separate here on purpose (SEC-5).
 *
 * The response echoes the persisted set, deduplicated. It is not used to patch
 * the table: the caller refetches, the same as every other mutation here.
 *
 * **An admin target is refused with a 409 worth showing** — admins reach every
 * space implicitly (PERM-2), and the message names the remedy.
 */
export const grantInputSchema = z.object({ spaceId: z.string(), mode: grantModeSchema });

export type GrantInput = z.infer<typeof grantInputSchema>;

export const memberGrantsSchema = z.object({
  userId: z.string(),
  grants: z.array(grantInputSchema),
});

export type MemberGrants = z.infer<typeof memberGrantsSchema>;

export function setMemberGrants(
  userId: string,
  grants: readonly GrantInput[],
): Promise<MemberGrants> {
  return putJson(`/users/${encodeURIComponent(userId)}/grants`, memberGrantsSchema, { grants });
}

/**
 * `POST /users/invite` ⚑A — put a member on the agency (USR-2).
 *
 * The only way anybody joins an agency, so the Members tab's other five actions
 * all presuppose it. Three things about it are easy to get wrong from the client
 * side:
 *
 * - **The address must be on the agency's own domain** (USR-2). The API refuses
 *   otherwise, with a message naming the domain — worth showing, because it is
 *   the caller's own and the alternative is retyping until one is accepted.
 * - **An address already in use is a 409**, not a silent overwrite. USR-3 never
 *   deletes a record, so a revoked colleague is reactivated rather than
 *   re-invited, and the message says so.
 * - **There is no name to send** (ADR-0038). The invitation carries an address;
 *   the roster shows that address until the member first signs in, and their
 *   own directory display name replaces it then. A 502 means Entra would not
 *   take the invitation — show the API's own message rather than the status.
 *
 * The response is not used to patch the table — the caller refetches, the same
 * as every other mutation here.
 */
export interface InviteMemberRequest {
  /** Must be at the agency's own domain. Validated here as a courtesy, and by the API. */
  email: string;
  cell?: string;
  /** Initial grants, each with its access level (PERM-1a). Optional per USR-2. */
  grants: readonly GrantInput[];
}

export const memberInvitedSchema = z.object({
  userId: z.string(),
  email: z.string(),
  /** Always `Invited` — SYS-6 makes redemption the only thing that activates. */
  status: z.enum(['Invited', 'Active', 'Revoked']),
  grants: z.array(grantInputSchema),
});

export type MemberInvited = z.infer<typeof memberInvitedSchema>;

export function inviteMember(body: InviteMemberRequest): Promise<MemberInvited> {
  return postJson('/users/invite', memberInvitedSchema, body);
}

/**
 * `POST /spaces` — mirrors `SpaceCreatedBody` in apps/api/src/functions/spaceCreate.
 *
 * **Opening the room, which is now its own act** (CON-5, CON-22, ADR-0071).
 * `POST /connect` used to do this as a side effect of naming a counterpart,
 * because the shared thing had no existence apart from the pair that made it. It
 * has a name and an owner now, so it is created on its own — and this is where
 * CON-7a's subscription gate lives.
 */
export const spaceViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  ownerAgencyId: z.string(),
  createdAt: z.string(),
  lastActivityAt: z.string().optional(),
  /** The room's active window in days (RET-12); absent means the 180-day default. */
  expiryWindowDays: z.number().int().optional(),
});

export type SpaceView = z.infer<typeof spaceViewSchema>;

export const spaceCreatedSchema = z.object({ space: spaceViewSchema });

export function createSpace(name: string): Promise<SpaceView> {
  return postJson('/spaces', spaceCreatedSchema, { name }).then((body) => body.space);
}

export const spaceRenamedSchema = z.object({ space: spaceViewSchema });

/**
 * `PATCH /spaces/{id}` — rename a room (CON-22, CON-25, **ADR-0102**).
 *
 * **The owner's act, and it answers 403 rather than 404 for a fellow member's
 * admin** — the one refusal in this file whose status code is deliberately the
 * unusual one. The roster has already disclosed the room's name and its owner
 * to every member (CON-11 as amended), so an absence would contradict the
 * screen the caller is looking at. A stranger still gets the 404.
 *
 * Nothing is derived from a space's name — no address, no container, no blob
 * path (SYS-3) — so a rename ripples nowhere and every surface picks it up on
 * its next read. Which is why the caller here refetches and patches nothing,
 * §4's standing rule.
 */
export function renameSpace(spaceId: string, name: string): Promise<SpaceView> {
  return patchJson(`/spaces/${encodeURIComponent(spaceId)}`, spaceRenamedSchema, { name }).then(
    (body) => body.space,
  );
}

export const spaceWindowSetSchema = z.object({
  spaceId: z.string(),
  expiryWindowDays: z.number().int(),
  /**
   * How many active files the change brings due (RET-12) — the blast radius the
   * owner sees before it happens. Zero on a reset to a longer/equal window.
   */
  filesNowDue: z.number().int(),
});

export type SpaceWindowSet = z.infer<typeof spaceWindowSetSchema>;

/**
 * `PATCH /spaces/{id}/window` — shorten (or reset) the room's active window
 * (RET-12).
 *
 * **The owner's act**, and it answers 403 for a fellow member's admin and 404
 * for a stranger, exactly as `renameSpace` does. The window only shortens: the
 * API bounds it to 1–180, and storing 180 resets a room to the default. The
 * response reports `filesNowDue` so a shorten can be confirmed against its
 * effect. Nothing is patched locally — the caller refetches, §4's standing rule.
 */
export function setSpaceWindow(spaceId: string, expiryWindowDays: number): Promise<SpaceWindowSet> {
  return patchJson(`/spaces/${encodeURIComponent(spaceId)}/window`, spaceWindowSetSchema, {
    expiryWindowDays,
  });
}

/**
 * `POST /spaces/{id}/members` — mirrors `SpaceMemberInvitedBody`.
 *
 * **`POST /connect`'s body, addressed to a room that already exists.** One
 * request, two outcomes (SPEC §4.2). `branch` says which happened, and it is
 * worth surfacing: `invited` means the agency did not exist and has been created
 * with an invitation outstanding, which reads identically to `existing` on the
 * roster but means something quite different to the admin who is now waiting on
 * a stranger to sign in.
 */
export interface AddSpaceMemberRequest {
  /**
   * The agency to add. **An id**, chosen from `GET /agencies/connections`.
   *
   * It was an `email`, and the domain it carried was what CON-7's branch B
   * minted an agency out of. Agencies come from the operator now (ADR-0095),
   * so the address was a worse way to name one that already exists.
   */
  agencyId: string;
  /** The admin's attestation about that agency (CON-9). Never defaulted. */
  cjiCleared: boolean;
  /**
   * What this agency will see in the room (CON-24).
   *
   * **Optional here and defaulted by the form rather than by the wire.** The API
   * reads an absent value as `all` — the room's whole contents, which is what a
   * room was before CON-24 — so omitting it is a valid request. `AddAgencyPanel`
   * sends one anyway: an owner choosing at invite time is choosing, and a form
   * that quietly omits its own default is a form whose state and whose request
   * can disagree.
   */
  visibilityScope?: VisibilityScope;
}

/**
 * **`branch` is gone from this body** (ADR-0095). It reported which of CON-7's
 * two paths ran, and there is one path — so a client that rendered two
 * different sentences from it now has one thing to say.
 */
export const spaceMemberInvitedSchema = z.object({
  spaceId: z.string(),
  agency: z.object({
    id: z.string(),
    name: z.string(),
    domain: z.string(),
  }),
});

export type SpaceMemberInvited = z.infer<typeof spaceMemberInvitedSchema>;

/**
 * `GET /agencies/connections` ⚑A — the agencies this one may add (CON-28).
 *
 * The picker's whole source. Three display fields and nothing commercial; the
 * operator curates the entries (CON-27), so a client cannot widen this.
 */
export const agencyConnectionSchema = z.object({
  agencyId: z.string(),
  name: z.string(),
  domain: z.string(),
  agencyType: z.string(),
});

export const agencyConnectionsSchema = z.object({
  connections: z.array(agencyConnectionSchema),
});

export type AgencyConnection = z.infer<typeof agencyConnectionSchema>;

export function getAgencyConnections(signal?: AbortSignal): Promise<{
  connections: AgencyConnection[];
}> {
  return request('/agencies/connections', agencyConnectionsSchema, signal ? { signal } : undefined);
}

export function inviteSpaceMember(
  spaceId: string,
  body: AddSpaceMemberRequest,
): Promise<SpaceMemberInvited> {
  return postJson(`/spaces/${encodeURIComponent(spaceId)}/members`, spaceMemberInvitedSchema, body);
}

/**
 * `POST /spaces/{id}/accept` and `DELETE /spaces/{id}/members/{aid}` — mirrors
 * `SpaceActionBody`.
 *
 * Both return only the space's new status. Everything else the list shows —
 * direction, last activity, whether an acceptance is still owed — moves with
 * that status, so the caller refetches rather than patching a row in place.
 */
export const spaceActionSchema = z.object({
  spaceId: z.string(),
  status: z.enum(['Invited', 'Active', 'Removed']),
});

export type SpaceAction = z.infer<typeof spaceActionSchema>;

export function acceptMembership(spaceId: string): Promise<SpaceAction> {
  return postJson(`/spaces/${encodeURIComponent(spaceId)}/accept`, spaceActionSchema);
}

/**
 * `DELETE /spaces/{id}` — end an empty room (CON-26).
 *
 * **Owner-only and empty-only**, and neither is this client's to decide: the
 * API answers 403 for a member that does not own the room and 409 while any
 * file survives unpurged. The screen gates on `isOwner` for visibility, exactly
 * as it does for adding an agency (SEC-5).
 *
 * It answers with the room's name rather than a `204`, on this transport's
 * standing rule — see `delJson`.
 */
export const spaceDeletedSchema = z.object({
  spaceId: z.string(),
  name: z.string(),
});

export type SpaceDeleted = z.infer<typeof spaceDeletedSchema>;

export function deleteSpace(spaceId: string): Promise<SpaceDeleted> {
  return delJson(`/spaces/${encodeURIComponent(spaceId)}`, spaceDeletedSchema);
}

/**
 * End one membership (CON-14).
 *
 * **The agency id is required rather than implied**, which is what lets one
 * route carry both of CON-14's verbs: pass your own to leave, and the owner
 * passes anybody's to remove them (ADR-0071). It was `POST /spaces/{id}/sever`
 * while only the first existed — a shape that could not express the second.
 */
export function removeMembership(spaceId: string, agencyId: string): Promise<SpaceAction> {
  return delJson(
    `/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(agencyId)}`,
    spaceActionSchema,
  );
}

/**
 * `PATCH /spaces/{id}/members/{aid}` — set one member's visibility scope
 * (CON-24, CON-25). Mirrors `SpaceMemberScopeBody`.
 *
 * **The owner's act, and one of §5.3's owner-only rows.** It shares a
 * route with `removeMembership` above and differs by method: one ends a
 * membership, the other changes what it admits, and both address the member by
 * agency id because a space has a roster rather than a counterpart.
 *
 * **The caller refetches rather than patching the row it sent.** A widening
 * reveals files that were already there and a narrowing hides them again
 * (CON-25) — so the folder counts, the file tables and the roster all move at
 * once, on the next read, and the server's answer is the only one worth
 * rendering.
 */
export const spaceMemberScopeSchema = z.object({
  spaceId: z.string(),
  agencyId: z.string(),
  visibilityScope: z.enum(VISIBILITY_SCOPES),
});

export type SpaceMemberScope = z.infer<typeof spaceMemberScopeSchema>;

export function setMemberScope(
  spaceId: string,
  agencyId: string,
  visibilityScope: VisibilityScope,
): Promise<SpaceMemberScope> {
  return patchJson(
    `/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(agencyId)}`,
    spaceMemberScopeSchema,
    { visibilityScope },
  );
}

/**
 * `GET /spaces/{id}/folders` — the shared space root (FIL-2).
 *
 * The response carries the space as well as its folders, and that is not
 * redundancy with `GET /spaces`: CON-15 makes the root a URL somebody can be
 * sent, so a caller may open this having never loaded a space list. Everything
 * the header needs comes from here.
 *
 * `canWrite` is the server's answer to "may this caller add to this space"
 * (CON-13, §5.3). It decides what the UI **offers**, never what it is allowed
 * to do — the API re-derives it on every write (SEC-5).
 */
/**
 * RET-6's three purchasable classes, plus the `None` that means unretained.
 *
 * `None` is not a fourth product — it is the value the API writes to express
 * absence, because a merge cannot remove a property (ADR-0021). Read it as "on
 * the ordinary 180-day active window".
 *
 * Declared least-first, which is the order a picker offers them (RET-6) —
 * `FolderView` and `FileRows` read `.options` rather than keeping a copy.
 */
export const retentionClassSchema = z.enum(['None', 'Extend', 'Preserve', 'Statutory']);

export type RetentionClass = z.infer<typeof retentionClassSchema>;

/**
 * Where a file's bytes are (RET-6, RET-11).
 *
 * Four Azure access tiers plus `Rehydrating`, which is not one — it names a
 * retrieval somebody asked for that Azure has not finished (RET-9). The client
 * never sets any of these: a tier follows a purchased retention class, and §14
 * forbids a per-file tier control.
 *
 * **No `TierState` alias accompanies it**, unlike `RetentionClass` above. That
 * one has consumers — `FolderView` passes a class to `setFileRetention` — while
 * nothing here ever *constructs* a tier: the client only reads
 * `FileSummary['tierState']`, which the inferred row type already supplies. An
 * exported type with no consumer is what `knip` is for.
 */
export const tierStateSchema = z.enum(['Hot', 'Cool', 'Cold', 'Archive', 'Rehydrating']);

export const folderSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  fileCount: z.number(),
  /**
   * The folder's weight, denormalized on the row server-side so the grid can
   * state it without reading a single file entry (FIL-2).
   *
   * **Bytes over the folder's Active files**, matching what `fileCount` counts —
   * a soft-deleted file has left the folder view and its bytes have left this
   * with it. Rendered binary, which is what a file size is (`BINARY` is
   * `formatBytes`'s default).
   */
  totalBytes: z.number(),
  lastActivityAt: z.string(),
  /**
   * Whether **this caller** asked to be told when the counterpart adds files
   * here (NTF-1).
   *
   * It rides on the listing so the folder header can render its switch without
   * a request per folder.
   *
   * **Defaulted rather than required**, unlike everything above it: the flag
   * arrived after this schema did, and a client built against an older API
   * would otherwise fail to parse a folder list entirely — a blank Files
   * surface rather than a missing switch. `false` is the honest reading of an
   * API that does not know what a subscription is.
   */
  subscribed: z.boolean().default(false),
  /**
   * Whether **this caller** may rename or delete this folder (FIL-3a, FIL-3b).
   *
   * One boolean for both verbs, because the API gates them identically: an
   * admin with write access at the agency that created the folder (ADR-0088).
   * **Visibility, not authorization** — SEC-5 still decides
   * on the request itself, so hiding the menu is a courtesy.
   *
   * **Required, not defaulted, and cutting against `subscribed` above it
   * deliberately.** `fileSummarySchema.canDelete` is the sibling to read: a
   * silent `false` renders every folder as unmanageable, which is a permissions
   * bug that looks like a product decision rather than a parse failure. A mock's
   * shape is part of the contract (Article 5), so a handler that forgot this
   * should fail loudly here.
   *
   * **Do not derive delete's availability from `fileCount`.** That count is the
   * *viewer's* (ADR-0069): an `own`-scope agency reads zero on a folder full of
   * a counterpart's files, so gating on it would offer delete on exactly the
   * folders FIL-3b must refuse. Offer it whenever this is true and render the
   * API's 409 when it comes.
   */
  canManage: z.boolean(),
});

export type FolderSummary = z.infer<typeof folderSummarySchema>;

export const spaceSchema = z.object({
  spaceId: z.string(),
  /**
   * The room's name (CON-22) — the page title.
   *
   * Required for `spaceSummarySchema.name`'s reason. It has to arrive on this
   * call rather than be looked up from the space list, because somebody can
   * open a space root from a pasted URL having never loaded one (CON-15).
   */
  name: z.string(),
  members: z.array(spaceMemberRefSchema),
  participation: z.enum(['contributor', 'observer']),
  canWrite: z.boolean(),
  /**
   * The server's answer to "may this caller apply a class over a folder here"
   * (RET-5, RET-7, PERM-6).
   *
   * On the space rather than derived from the rows, because an empty folder — or
   * one holding only the counterpart's uploads — has no row to derive it from,
   * and the control would vanish exactly when the folder is empty enough to want
   * it. **Visibility, not authorization**, as `canWrite` above.
   */
  canSetRetention: z.boolean(),
  /**
   * The room's active window in days (RET-12); absent means the 180-day default.
   * The folder view reads it to render the "expiring soon" hint on file rows.
   */
  expiryWindowDays: z.number().int().optional(),
});

export type Space = z.infer<typeof spaceSchema>;

export const spaceBodySchema = z.object({
  space: spaceSchema,
  folders: z.array(folderSummarySchema),
});

export type SpaceBody = z.infer<typeof spaceBodySchema>;

export function getFolders(spaceId: string, signal?: AbortSignal): Promise<SpaceBody> {
  return request(
    `/spaces/${encodeURIComponent(spaceId)}/folders`,
    spaceBodySchema,
    signal ? { signal } : undefined,
  );
}

/** `POST /spaces/{id}/folders` — admin-only folder creation (FIL-3). Name only. */
export const folderCreatedSchema = z.object({ folder: folderSummarySchema });

export type FolderCreated = z.infer<typeof folderCreatedSchema>;

export function createFolder(spaceId: string, name: string): Promise<FolderCreated> {
  return postJson(`/spaces/${encodeURIComponent(spaceId)}/folders`, folderCreatedSchema, { name });
}

/**
 * `PATCH /spaces/{id}/folders/{fid}` — rename a folder (FIL-3a). Name only.
 *
 * Refused with a **403** for anybody outside the agency that created it — never
 * a 404, because the folder is on the caller's own grid with its name on it. A
 * duplicate name is a **409**. Both carry the API's own wording, which names a
 * remedy the reader can act on; surface it rather than a generic failure.
 */
export const folderRenamedSchema = z.object({ folder: folderSummarySchema });

export type FolderRenamed = z.infer<typeof folderRenamedSchema>;

export function renameFolder(
  spaceId: string,
  folderId: string,
  name: string,
): Promise<FolderRenamed> {
  return patchJson(
    `/spaces/${encodeURIComponent(spaceId)}/folders/${encodeURIComponent(folderId)}`,
    folderRenamedSchema,
    { name },
  );
}

/**
 * `DELETE /spaces/{id}/folders/{fid}` — remove a folder (FIL-3b).
 *
 * **A 409 unless the folder is empty of every member's files**, including any
 * sitting in a recycle bin (PERM-4, FIL-11). The client cannot predict that — the
 * count it holds is the viewer's own — so the control is offered on
 * `canManage` alone and this refusal is what says the rest. Render the API's
 * message: it names both halves of the remedy.
 *
 * It answers a body rather than a 204, and the name in it is what the folder was
 * called: after this act there is no row left to resolve the id against.
 */
export const folderDeletedSchema = z.object({ folderId: z.string(), name: z.string() });

export type FolderDeleted = z.infer<typeof folderDeletedSchema>;

export function deleteFolder(spaceId: string, folderId: string): Promise<FolderDeleted> {
  return delJson(
    `/spaces/${encodeURIComponent(spaceId)}/folders/${encodeURIComponent(folderId)}`,
    folderDeletedSchema,
  );
}

/**
 * `GET /spaces/{id}/folders/{fid}/files` — the file table (FIL-4..6).
 *
 * Two things about the rows are worth knowing from the client side:
 *
 * - **`id` is an opaque handle**, not a bare file id. It carries the partition
 *   the download route needs (SPEC §11). Hand it back untouched; never build one.
 * - **`uploadedBy` names a person at an agency, on both sides of the space**
 *   (FIL-5). It is the one thing that crosses the boundary CON-11 otherwise
 *   draws around a counterpart's users, and it is why a file table is a record
 *   rather than a list of names of files.
 */
export const fileSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['PDF', 'VIDEO', 'PHOTOS', 'AUDIO', 'ARCHIVE', 'PHONE_DUMP', 'DOC', 'OTHER']),
  sizeBytes: z.number(),
  /** Absent until the ingest job has run; `hashStatus` is what says so (UPL-6). */
  sha256: z.string().optional(),
  hashStatus: z.enum(['pending', 'ok', 'mismatch']),
  uploadedBy: z.object({ name: z.string(), agencyName: z.string() }),
  uploadedAt: z.string(),
  /**
   * The server's answer to "may this caller delete this row" (§5.3, PERM-4).
   *
   * `space.canWrite` at row grain, and it carries the same warning: it decides
   * what the table **offers**, never what anyone is allowed to do. The API
   * applies the same rule to the request itself (SEC-5).
   *
   * Required rather than optional-with-a-default, deliberately: a mock's shape
   * is part of the contract (Article 5), so a handler that forgot this should
   * fail loudly here rather than quietly render every row as undeletable.
   */
  canDelete: z.boolean(),
  /**
   * The purchased retention class exempting this file from the active window
   * (RET-4, RET-6). `None` means the file is on the ordinary 180-day window.
   *
   * Total on the wire, so the rendering is exhaustive without a default this
   * schema would have to invent.
   */
  retentionClass: retentionClassSchema,
  /**
   * When the class lapses. **Absent whenever `retentionClass` is `None`**, and
   * the API is what guarantees that: a released row keeps a stale date, because
   * a merge cannot remove a property, and the projection is where it is stopped
   * (ADR-0021). Nothing here should ever read this without checking the class.
   */
  retentionUntil: z.string().optional(),
  /**
   * The server's answer to "may this caller set retention on this row"
   * (§5.3, PERM-6).
   *
   * `canDelete`'s sibling and **not its twin**: a member may delete their own
   * upload and may never retain one, because the act commits money the member
   * cannot commit. Required for `canDelete`'s reason.
   */
  canSetRetention: z.boolean(),
  /**
   * Where this file's bytes are, and therefore whether Download will work
   * (RET-6, RET-9, RET-11).
   *
   * **The one row property that is a fact about the file rather than about the
   * caller**, and that is why there is no `canRehydrate` beside the two flags
   * above. §5.3's rehydration row is ✓/✓/✓ — everyone who can see a row can ask
   * for it back — so a permission flag would be a constant `true` dressed as a
   * decision, and it would have to be read by a read-only member who answers `false` to
   * both of the others.
   *
   * `Cool` and `Cold` are **online**: `Extend` and `Preserve` download instantly
   * (RET-6). Only `Archive` needs retrieval, and only `Rehydrating` means one is
   * already under way. Required for `canDelete`'s reason.
   */
  tierState: tierStateSchema,
  /**
   * When this file's active window closes (RET-1, RET-12) — the table renders an
   * "expiring soon" hint from it. Sweep-maintained and optional: absent on a row
   * uploaded since the last sweep, and after a room shortens its window it can
   * trail by up to a night (a soft hint, not an authorization).
   */
  expiresAt: z.string().optional(),
});

export type FileSummary = z.infer<typeof fileSummarySchema>;

export const filesBodySchema = z.object({
  files: z.array(fileSummarySchema),
  /** Absent when the folder is exhausted. */
  continuationToken: z.string().optional(),
});

export type FilesBody = z.infer<typeof filesBodySchema>;

export function getFiles(
  spaceId: string,
  folderId: string,
  continuationToken?: string,
  signal?: AbortSignal,
): Promise<FilesBody> {
  const query =
    continuationToken === undefined
      ? ''
      : `?continuationToken=${encodeURIComponent(continuationToken)}`;

  return request(
    `/spaces/${encodeURIComponent(spaceId)}/folders/${encodeURIComponent(folderId)}/files${query}`,
    filesBodySchema,
    signal ? { signal } : undefined,
  );
}

/**
 * `GET /files/{id}/download` — the audited SAS (FIL-8, UPL-8).
 *
 * **A body, not a redirect.** SEC-3 keeps the credential out of browser history
 * and out of every proxy log between here and the storage account, so the API
 * hands back a URL and the caller follows it.
 *
 * Fetch this at the moment of the click and use it immediately: it expires in
 * minutes, and it is never worth storing.
 */
export const downloadSchema = z.object({
  url: z.string(),
  expiresAt: z.string(),
  /** The blob path is ids all the way down, so the real name comes from here. */
  fileName: z.string(),
});

export type Download = z.infer<typeof downloadSchema>;

/**
 * The `signal` is not optional politeness — it is what stops a fast `→ → →`
 * through the image viewer from leaving three mints in flight. Each one is an
 * audited, metered, capped act (UPL-8), so an abandoned request is a
 * `file.downloaded` row for a photograph nobody ended up looking at.
 */
export function getDownload(fileId: string, signal?: AbortSignal): Promise<Download> {
  return request(
    `/files/${encodeURIComponent(fileId)}/download`,
    downloadSchema,
    signal ? { signal } : undefined,
  );
}

/**
 * `DELETE /files/{id}` — soft delete into the agency's recycle bin (FIL-10).
 *
 * Nothing is destroyed. The file leaves the folder view, keeps its row and its
 * bytes, and is restorable for 30 days before the purge job takes the blob
 * (UPL-10). The API answers with the row's new shape rather than a `204`, for
 * the reason `delJson` documents.
 *
 * One schema serves both directions of the lifecycle, because the two responses
 * differ only in the fields below.
 */
export const fileStateSchema = z.object({
  /** The same handle that was sent: a lifecycle change never moves the row key. */
  id: z.string(),
  state: z.enum(['Active', 'Deleted', 'Purged']),
  /** Present after a delete, absent after a restore. */
  deletedAt: z.string().optional(),
});

export type FileStateResult = z.infer<typeof fileStateSchema>;

export function deleteFile(fileId: string): Promise<FileStateResult> {
  return delJson(`/files/${encodeURIComponent(fileId)}`, fileStateSchema);
}

/**
 * `POST /files/{id}/restore` — put a soft-deleted file back (FIL-11).
 *
 * Takes the handle the recycle bin handed out, which is the same handle the
 * folder view's delete used: a lifecycle change never moves the row key.
 *
 * Its refusals are worth showing verbatim through `messageFrom`. A 403 names the
 * rule the caller can act on — restore keys on who *deleted* a file, not who
 * uploaded it (§5.3) — and a 409 means somebody else already put it back.
 */
export function restoreFile(fileId: string): Promise<FileStateResult> {
  return postJson(`/files/${encodeURIComponent(fileId)}/restore`, fileStateSchema);
}

/**
 * `POST /files/{id}/retention` ⚑A — buy, change, or release a class (RET-4..8).
 *
 * Its refusals carry more than any fallback line could: a **403** says the
 * agency needs the Evidence add-on and how to get it (RET-5), and a **409**
 * names the date before which Azure charges the full tier minimum regardless
 * (RET-8). Show them through `messageFrom`.
 */
export const fileRetentionSchema = z.object({
  /** The same handle that was sent: retention never moves the row key. */
  id: z.string(),
  retentionClass: retentionClassSchema,
  /** Absent for `None`, and absent even where the row keeps a stale one. */
  retentionUntil: z.string().optional(),
  /** False when the file already held the class and nothing was written. */
  changed: z.boolean(),
});

export type FileRetentionResult = z.infer<typeof fileRetentionSchema>;

export function setFileRetention(
  fileId: string,
  retentionClass: RetentionClass,
): Promise<FileRetentionResult> {
  return postJson(`/files/${encodeURIComponent(fileId)}/retention`, fileRetentionSchema, {
    retentionClass,
  });
}

/**
 * `POST /spaces/{id}/folders/{fid}/retention` ⚑A — apply a class over a folder
 * (RET-7).
 *
 * **It answers 200 even when rows failed**, because it is a report rather than a
 * refusal: the act is best-effort per file, so one row refusing on RET-8's
 * minimum must not deny the rest of the folder (A-7). Render `failed` — an empty
 * array is the ordinary case, not the only one.
 *
 * `skipped` counts two unlike things on purpose: the counterpart's rows, which
 * are not this admin's to change (PERM-4), and rows already in the class.
 */
export const folderRetentionSchema = z.object({
  folderId: z.string(),
  retentionClass: retentionClassSchema,
  applied: z.number(),
  skipped: z.number(),
  failed: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      message: z.string(),
    }),
  ),
});

export type FolderRetentionResult = z.infer<typeof folderRetentionSchema>;

export function setFolderRetention(
  spaceId: string,
  folderId: string,
  retentionClass: RetentionClass,
): Promise<FolderRetentionResult> {
  return postJson(
    `/spaces/${encodeURIComponent(spaceId)}/folders/${encodeURIComponent(folderId)}/retention`,
    folderRetentionSchema,
    { retentionClass },
  );
}

/**
 * `POST /files/{id}/rehydrate` — ask for an archived file back (RET-9).
 *
 * **The one file-scoped act in this client that is not admin-only**, and the
 * only one a read-only member can perform. PERM-6: an archived file is unreadable until
 * somebody asks, and the reader who needs it is often a defender on a discovery
 * deadline — a fellow member, or a reader whose agency holds no plan at all. So nothing here
 * is gated on `canDelete` or `canSetRetention`; the offer comes from
 * `tierState`, which is a fact about the file.
 *
 * **Every outcome is a 200**, including "it is already readable". RET-9's
 * premise is that the reader cannot see the tier, so refusing a request that has
 * already been satisfied would answer a question nobody asked — and asking twice
 * while a retrieval is in flight answers `in_progress` rather than starting a
 * second one, so a double click costs the uploading agency nothing.
 *
 * There is no ETA in the response and there must not be one: Azure supplies
 * none. "A few hours" is what the copy says, because it is what RET-9 sold.
 */
export const fileRehydrateSchema = z.object({
  /** The same handle that was sent: a retrieval never moves the row key. */
  id: z.string(),
  status: z.enum(['started', 'in_progress', 'available']),
  tierState: tierStateSchema,
  /** When the file returns to its class's tier. Present only on `available`. */
  rehydratedUntil: z.string().optional(),
});

export type FileRehydrateResult = z.infer<typeof fileRehydrateSchema>;

export function requestRehydration(fileId: string): Promise<FileRehydrateResult> {
  return postJson(`/files/${encodeURIComponent(fileId)}/rehydrate`, fileRehydrateSchema);
}

/**
 * `GET /recycle-bin` — the agency's soft-deleted files, across every space
 * (FIL-11).
 *
 * **It takes no arguments, and that is the contract rather than an omission.**
 * The bin is per agency, and the agency is resolved server-side from the token —
 * the client never names one (SEC-5).
 *
 * The rows are not `FileSummary`. They carry no `canDelete` (a deleted row could
 * only ever answer that with a lie) and they add where the file came from, who
 * removed it, and how long is left.
 */
export const deletedFileSchema = z.object({
  /** The opaque handle `restoreFile` takes. Hand it back untouched. */
  id: z.string(),
  name: z.string(),
  kind: fileSummarySchema.shape.kind,
  sizeBytes: z.number(),
  sha256: z.string().optional(),
  hashStatus: fileSummarySchema.shape.hashStatus,
  uploadedBy: z.object({ name: z.string(), agencyName: z.string() }),
  uploadedAt: z.string(),
  /**
   * Where it came from. The bin spans spaces, so a file name alone does not say
   * enough to find it again — and the ids are what the row spaces back through.
   */
  origin: z.object({
    spaceId: z.string(),
    /** The room's own name (CON-22), not the counterpart's. */
    spaceName: z.string(),
    folderId: z.string(),
    folderName: z.string(),
  }),
  /** A display name. No agency beside it: a deletion never crosses a space (PERM-4). */
  deletedBy: z.string(),
  deletedAt: z.string(),
  /**
   * Whole days before the purge job takes it (UPL-10).
   *
   * Clamped server-side, and `.min(0)` makes the clamp part of the contract:
   * purge is a job rather than a clock, so a file past its window reads `0` —
   * "goes on the next sweep" — and never a negative number.
   */
  daysRemaining: z.number().int().min(0),
  /**
   * The server's answer to "may this caller restore this row" (§5.3, FIL-11a).
   *
   * Required rather than optional-with-a-default, on `canDelete`'s argument: a
   * stale handler should fail loudly here rather than quietly render every row
   * as unrestorable.
   */
  canRestore: z.boolean(),
});

export type DeletedFile = z.infer<typeof deletedFileSchema>;

export const recycleBinSchema = z.object({
  files: z.array(deletedFileSchema),
  /** True when the read hit its ceiling — the bin is showing a prefix, not the set. */
  truncated: z.boolean(),
});

export type RecycleBin = z.infer<typeof recycleBinSchema>;

export function getRecycleBin(signal?: AbortSignal): Promise<RecycleBin> {
  return request('/recycle-bin', recycleBinSchema, signal ? { signal } : undefined);
}

/**
 * `POST /recycle-bin/purge` — end the thirty days early (FIL-11b).
 *
 * Takes no file id: the act is "empty my bin", and the set it empties is the
 * one the GET showed with `canRestore: true`.
 *
 * **Both numbers matter.** `remaining` is what the caller could *not* destroy —
 * rows an administrator at their own agency deleted — so a screen reporting
 * only `purged` would say the bin is empty and then render what is left.
 */
export const recycleBinPurgeSchema = z.object({
  purged: z.number(),
  remaining: z.number(),
});

export type RecycleBinPurge = z.infer<typeof recycleBinPurgeSchema>;

export function purgeRecycleBin(signal?: AbortSignal): Promise<RecycleBinPurge> {
  return request('/recycle-bin/purge', recycleBinPurgeSchema, {
    method: 'POST',
    ...(signal ? { signal } : {}),
  });
}

/**
 * `GET /audit` ⚑A — the agency's chain-of-custody log (SPEC §8; AUD-3, AUD-5).
 *
 * Mirrors `AuditBody` in `apps/api/src/functions/audit`. Two shapes here are
 * unlike anything else in this file, and both are deliberate:
 *
 * - **`action` is a plain string, not an enum.** Everywhere else a stale shape
 *   fails loudly, because that is what keeps the two hand-mirrored workspaces
 *   honest. AUD-1 makes every audit row permanent and AUD-6's catalog grows with
 *   the spec, so a log is the one surface that must render a row it does not
 *   recognise rather than refuse the page. The copy map falls back to the raw
 *   value. Everything closed by the *data model* rather than by the catalog —
 *   `eventClass`, `target.type`, `dayBucketed` — stays strict.
 * - **`continuationToken` present with an empty `events` means keep going.** It
 *   is absent only when the log is exhausted; the server scans a bounded number
 *   of rows per request and a selective filter can match none of them. A client
 *   that stops on an empty page silently truncates.
 */
export const auditEventSchema = z.object({
  /** The `eventId`. The same id appears in both partitions of an AUD-4 dual write. */
  id: z.string(),
  occurredAt: z.string(),
  /** Deliberately not an enum — see the note above. */
  action: z.string(),
  /** AUD-5's class, derived server-side from the actor's agency vs the partition. */
  eventClass: z.enum(['agency', 'counterpart']),
  actor: z.object({
    /** An address, or `Blue Partner` on an operator action. */
    email: z.string(),
    agencyName: z.string(),
    /** Server-computed, so the sentinel is not re-declared on this side. */
    isSystem: z.boolean(),
  }),
  target: z.object({
    /**
     * `AUDIT_TARGET_TYPES` in the API's `types/domain.ts`, hand-mirrored — there
     * is no shared types package.
     *
     * **`link` and `space` both, and neither may be dropped.** M14 renamed the
     * concept, and AUD-1 makes every row permanent: rows written before the
     * rename say `link` forever and there is no migration that could reach them.
     * `space` was missing here while nine API writers emitted it, which refused
     * the whole page rather than the row — the failure this comment exists to
     * stop being reintroduced by "tidying" the union.
     */
    type: z.enum(['file', 'folder', 'user', 'link', 'space', 'agency']),
    id: z.string(),
  }),
  /** Absent on an event that is not about a space (`member.invited`, `agency.created`). */
  space: z.object({ spaceId: z.string(), name: z.string() }).optional(),
  /** AUD-2's small structured payload. Shape varies per action, so it stays open. */
  details: z.record(z.string(), z.unknown()).optional(),
  /**
   * AUD-6a: `space.opened` sorts as of the start of its UTC day while
   * `occurredAt` holds the true instant. The one row whose position in a
   * newest-first list is not its timestamp.
   */
  dayBucketed: z.boolean(),
});

export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditBodySchema = z.object({
  events: z.array(auditEventSchema),
  /** Absent **only** when the log is exhausted. See the note above. */
  continuationToken: z.string().optional(),
});

export type AuditBody = z.infer<typeof auditBodySchema>;

export interface AuditQuery {
  /** AUD-5's event class. `all` is the default and is sent as nothing. */
  eventClass?: 'all' | 'agency' | 'counterpart';
  spaceId?: string;
  /** Matched **exactly** and case-insensitively server-side, never as a fragment. */
  actorEmail?: string;
  continuationToken?: string;
}

export function getAudit(query: AuditQuery = {}, signal?: AbortSignal): Promise<AuditBody> {
  const params = new URLSearchParams();
  if (query.eventClass !== undefined && query.eventClass !== 'all') {
    params.set('class', query.eventClass);
  }
  if (query.spaceId !== undefined && query.spaceId !== '') params.set('spaceId', query.spaceId);
  if (query.actorEmail !== undefined && query.actorEmail !== '') {
    params.set('actor', query.actorEmail);
  }
  if (query.continuationToken !== undefined) {
    params.set('continuationToken', query.continuationToken);
  }

  const search = params.toString();
  return request(
    `/audit${search === '' ? '' : `?${search}`}`,
    auditBodySchema,
    signal ? { signal } : undefined,
  );
}

/**
 * The upload session (SPEC §7.1, UPL-1..5).
 *
 * **`upload.url` carries a SAS. It is a credential.** Never store it, never log
 * it, never render it (SEC-3). It is reissued on every call rather than held
 * anywhere, which is what makes that true rather than aspirational — the API
 * keeps none of it on the session row either.
 *
 * Two things about the shape are load-bearing:
 *
 * - **`blockSizeBytes` is the server's choice**, not a suggestion (UPL-3). The
 *   client slices at exactly that size; picking its own would produce a block
 *   list the API's size check then rejects.
 * - **`expiresAt` is why `PATCH` exists.** A multi-gigabyte transfer outlives
 *   any credential short enough to be safe, so the token is renewable rather
 *   than long-lived.
 */
export const uploadSessionViewSchema = z.object({
  session: z.object({
    id: z.string(),
    fileId: z.string(),
    status: z.enum(['pending', 'committed', 'finalized', 'abandoned']),
  }),
  upload: z.object({
    url: z.string(),
    expiresAt: z.string(),
    /**
     * Positive, because the block plan is `ceil(size / blockSizeBytes)` and a
     * zero would be an infinite one. A schema is a cheaper place to find that
     * than a wedged tab.
     */
    blockSizeBytes: z.number().int().positive(),
  }),
});

export type UploadSessionView = z.infer<typeof uploadSessionViewSchema>;

export interface OpenUploadSession {
  fileName: string;
  sizeBytes: number;
}

/** `POST /spaces/{id}/folders/{fid}/upload-session` — allocates the fileId, and so the blob path. */
export function createUploadSession(
  spaceId: string,
  folderId: string,
  body: OpenUploadSession,
): Promise<UploadSessionView> {
  return postJson(
    `/spaces/${encodeURIComponent(spaceId)}/folders/${encodeURIComponent(folderId)}/upload-session`,
    uploadSessionViewSchema,
    body,
  );
}

/**
 * `PATCH /upload-sessions/{sid}` — a fresh SAS, and optionally the commit.
 *
 * Called with no body to renew a credential that is close to expiring, and with
 * `{status: 'committed'}` once the block list is in. It deliberately reports
 * **nothing** about which blocks arrived: the API is not in the byte path
 * (UPL-1), so the client asks blob storage that directly with the credential it
 * is handed back.
 */
export function renewUploadSession(
  sessionId: string,
  body?: { status: 'committed' },
): Promise<UploadSessionView> {
  return patchJson(
    `/upload-sessions/${encodeURIComponent(sessionId)}`,
    uploadSessionViewSchema,
    body,
  );
}

/**
 * `POST /upload-sessions/{sid}/finalize` — the file becomes visible (UPL-5).
 *
 * **`file.id` here is the bare `fileId`, not the `{spaceId}_{rowKey}` handle that
 * `GET /files/{id}/download` takes** (SPEC §11). A caller that tried to download
 * with it would get a 404. So a finished upload **refetches the folder listing**
 * rather than splicing a row into the table — which is also what keeps the
 * server's ordering and the counterpart's uploads correct.
 *
 * `contentType` is the browser's guess, and it is advisory: the API derives the
 * file's `kind` from it, and `kind` is informational, never behavioral (FIL-4).
 */
export const fileFinalizedSchema = z.object({
  file: z.object({
    id: z.string(),
    name: z.string(),
    kind: fileSummarySchema.shape.kind,
    sizeBytes: z.number(),
    /** Always pending: the ingest job hashes it afterwards (UPL-6). */
    hashStatus: z.literal('pending'),
    uploadedAt: z.string(),
  }),
});

export type FileFinalized = z.infer<typeof fileFinalizedSchema>;

export function finalizeUpload(
  sessionId: string,
  /**
   * `signedFromFileId` is FIL-8c's provenance claim — the handle of the
   * document this one was signed from. The API resolves it through the caller's
   * own access before recording it, so an unreachable one refuses the finalize
   * rather than landing a file with an unchecked reference beside it.
   */
  body?: { contentType?: string; signedFromFileId?: string },
): Promise<FileFinalized> {
  return postJson(
    `/upload-sessions/${encodeURIComponent(sessionId)}/finalize`,
    fileFinalizedSchema,
    body,
  );
}

/**
 * The Storage surface's two reads (SPEC §10, §12).
 *
 * **Two endpoints and two hooks, not one of each.** The summary is a set of
 * totals and the usage series is a shape over time; they fail independently, and
 * a failed chart must not blank the allowance figures an admin came for. That is
 * `useAudit`'s rule and `FolderView`'s "supplementary `useFolders`" rule,
 * one surface over.
 *
 * **Every byte figure here is decimal against a decimal allowance.** The API's
 * `shared/plans.ts` states contract figures in 10^9 because that is what an
 * agency is invoiced against, so anything rendered beside `allowanceBytes` goes
 * through `formatBytes(…, DECIMAL)`. Mixing bases inside one sentence is a
 * silent 7% at GB and 10% at TB.
 */
export const storageWindowSchema = z.object({
  consumedBytes: z.number(),
  allowanceBytes: z.number(),
  state: z.enum(['ok', 'warning', 'exceeded']),
  /**
   * Whether crossing this window refuses a download (PLN-10, as amended).
   *
   * Sent by the server rather than inferred here: "which windows bite" is a
   * rule, and a rule in two places is a rule that drifts. `false` on the annual
   * window, whose overage is invoiced rather than refused (STO-1).
   */
  enforcing: z.boolean(),
});

export const storageSpaceRowSchema = z.object({
  spaceId: z.string(),
  /** The room's name (CON-22). It replaced the counterpart's name and id. */
  spaceName: z.string(),
  /**
   * Every status appears, `Removed` included — those bytes are still in the
   * agency's storage account and still cost it money (CON-14, UPL-12). This is
   * the inverse of the recycle bin's Active-only rule, which exists only
   * because the bin offers a Restore.
   */
  status: z.enum(['Invited', 'Active', 'Removed']),
  fileCount: z.number(),
  /** What this agency **uploaded** here (PLN-9). */
  bytes: z.number(),
  /** What this agency **read** from here (PLN-8). The two point opposite ways. */
  transferBytes: z.number(),
  /** Read out of this space by plan-less readers, billed to this agency (PLN-8). */
  recipientTransferBytes: z.number(),
  shareOfTotal: z.number(),
});

export type StorageSpaceRow = z.infer<typeof storageSpaceRowSchema>;

export const storageSummarySchema = z.object({
  plan: z.enum(['Docs', 'DocsEvidence']),
  band: z.enum(['B1', 'B2', 'B3', 'B4', 'B5']).nullable(),
  storage: z.object({
    fileCount: z.number(),
    bytes: z.number(),
    allowanceBytes: z.number(),
    /**
     * No `enforcing` beside it, unlike the transfer windows, and that asymmetry
     * is PLN-11's: storage is **soft always** — warn at 80%, notify at 100%,
     * never refuse an upload. A flag here would be a `false` nobody could ever
     * see turn true.
     */
    state: storageWindowSchema.shape.state,
  }),
  transfer: z.object({
    rolling7d: storageWindowSchema,
    rolling30d: storageWindowSchema,
    annual: storageWindowSchema,
  }),
  /** PLN-12's stage — what lets the surface say "would have applied" (STO-6). */
  enforcement: z.enum(['soft', 'throttling']),
  spaces: z.array(storageSpaceRowSchema),
  holdings: z.object({
    /** STO-3's first line. */
    recycleBin: z.object({ fileCount: z.number(), bytes: z.number() }),
    /**
     * STO-3's second line — the storage the agency has committed to
     * non-cancellably (RET-8).
     *
     * **Optional, and an absent field means Docs rather than zero** (RET-5). The
     * API withholds it for an agency that cannot buy a retention class at all,
     * so a zero here is a real measurement by an Evidence agency retaining
     * nothing. Rendering the two the same way would tell a Docs admin they have
     * nothing retained when the truthful answer is that they cannot.
     *
     * **These bytes are a subset of what is already reported above**, not an
     * addend to it: a retained file is Active (inside `storage.bytes`) or binned
     * (inside `recycleBin.bytes`). Folding it into the meter would double-count.
     */
    retention: z
      .object({
        fileCount: z.number(),
        bytes: z.number(),
        byClass: z.array(
          z.object({
            retentionClass: z.enum(['Extend', 'Preserve', 'Statutory']),
            fileCount: z.number(),
            bytes: z.number(),
          }),
        ),
      })
      .optional(),
  }),
  /** PLN-6's ceiling refusals — the Evidence upgrade signal, surfaced here. */
  rejectedForSize: z.object({
    count: z.number(),
    since: z.string(),
    truncated: z.boolean(),
  }),
  since: z.string(),
  /**
   * When the rollup last counted the **transfer** half, or null if never.
   *
   * The stored figures carry no such stamp because they were measured during
   * the request. Required rather than optional so a stale handler fails at this
   * boundary instead of quietly rendering a cached figure as a live one.
   */
  transferAsOf: z.string().nullable(),
});

export type StorageSummary = z.infer<typeof storageSummarySchema>;

export function getStorageSummary(signal?: AbortSignal): Promise<StorageSummary> {
  return request('/storage/summary', storageSummarySchema, signal ? { signal } : undefined);
}

export const storageUsagePointSchema = z.object({
  /** `YYYY-MM-DD`, one per UTC day of the term, oldest first and never skipped. */
  date: z.string(),
  transferBytes: z.number(),
  recipientTransferBytes: z.number(),
  /**
   * **Null means the day was never sampled, not that nothing was stored.**
   * Transfer is a flow, so an absent row genuinely moved zero bytes; storage is
   * a level, and a zero there would be a measurement nobody took. Render it as
   * a gap in the series.
   */
  storageBytes: z.number().nullable(),
});

export type StorageUsagePoint = z.infer<typeof storageUsagePointSchema>;

export const storageUsageSchema = z.object({
  series: z.array(storageUsagePointSchema),
  /**
   * The pair PLN-5's annual band review reads (STO-7) — computed server-side so
   * the operator's figure and the agency's screen are one number.
   */
  peaks: z.object({
    rolling30dTransferBytes: z.number(),
    storageBytes: z.number().nullable(),
  }),
  since: z.string(),
  until: z.string(),
});

export type StorageUsage = z.infer<typeof storageUsageSchema>;

export function getStorageUsage(signal?: AbortSignal): Promise<StorageUsage> {
  return request('/storage/usage', storageUsageSchema, signal ? { signal } : undefined);
}

/**
 * `POST /exports/manifest` ⚑A and `POST /exports/audit` ⚑A — the exit exports
 * (SPEC §19; EXIT-5, EXIT-6).
 *
 * Mirror `ManifestExportBody` and `AuditExportBody` in
 * `apps/api/src/functions/exportManifest` and `exportAudit`.
 *
 * **One call returns both renderings, and that is the contract rather than a
 * convenience.** EXIT-5 says the manifest is "delivered as CSV and JSON" — a
 * pair, generated once. A `format` parameter would mean two requests, two walks
 * of a moving table, and two documents describing two different instants; here
 * the CSV is serialized from the `rows` on the wire, so they cannot disagree.
 * `saveExport` writes both files from one response for exactly that reason.
 *
 * **`csv` already carries a UTF-8 BOM.** Do not add a second when building the
 * `Blob` — see `saveExport`.
 *
 * **`rows` stays loosely typed here.** The manifest's columns are fixed by
 * EXIT-5 and the audit export's by §11, but nothing on this side reads a field:
 * the surface renders a count and writes two files. Mirroring seventeen columns
 * so a component can ignore all of them would be a second copy of the API's
 * shape with no consumer to keep it honest.
 */
const exportEnvelopeSchema = z.object({
  generatedAt: z.string(),
  agencyId: z.string(),
  agencyName: z.string(),
  rowCount: z.number().int().min(0),
  rows: z.array(z.record(z.string(), z.unknown())),
  csv: z.string(),
});

export type ExportEnvelope = z.infer<typeof exportEnvelopeSchema>;

export function postManifestExport(): Promise<ExportEnvelope> {
  return postJson('/exports/manifest', exportEnvelopeSchema);
}

export function postAuditExport(): Promise<ExportEnvelope> {
  return postJson('/exports/audit', exportEnvelopeSchema);
}

// ---------------------------------------------------------------------------
// The agency's own scanners — SPEC USR-16, UPL-13, ADR-0098
// ---------------------------------------------------------------------------

/**
 * Hand-mirrored from `apps/api/src/functions/scanners`.
 *
 * **There is no field here that could carry a token or a digest**, and there
 * must never be one. A scanner's secret exists in the operator console's issue
 * response and on a Linux box; only `hashSecret` of it reaches a row, and this
 * read returns neither.
 */
const scannerSchema = z.object({
  scannerId: z.string(),
  name: z.string(),
  /** The box it is installed on, so a support call can name the machine. */
  deviceName: z.string(),
  spaceId: z.string(),
  spaceName: z.string(),
  /** What the uploader column shows on Files (FIL-5). */
  serviceUserName: z.string(),
  status: z.enum(['Active', 'Revoked']),
  createdAt: z.string(),
  /** Absent means it has never uploaded — a fact, not a missing value. */
  lastUsedAt: z.string().optional(),
  revokedAt: z.string().optional(),
});

const scannersSchema = z.object({ scanners: z.array(scannerSchema) });

export type Scanners = z.infer<typeof scannersSchema>;

/**
 * `GET /scanners` ⚑A — the agency's own scanners (USR-16).
 *
 * **Read-only, and there is deliberately no companion mutation.** Issue and
 * revoke are Blue Partner's in this release (OPS-20), so a compromised token is
 * a telephone call — which the screen says out loud rather than leaving an admin
 * to look for a button that is not there.
 */
export function getScanners(signal?: AbortSignal): Promise<Scanners> {
  return request('/scanners', scannersSchema, signal ? { signal } : undefined);
}
